import { Navigate, useLocation } from "react-router-dom";
import { useOrganisation } from "@/context/OrganisationContext";
import { useWebsites } from "@/context/WebsitesContext";
import { useState, useEffect } from "react";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOrg?: boolean;
}

export const ProtectedRoute = ({ children, requireOrg = true }: ProtectedRouteProps) => {
  const { hasOrganisation, isLoading: orgLoading } = useOrganisation();
  const { currentWebsite, isLoading: websitesLoading } = useWebsites();
  const [forceShowContent, setForceShowContent] = useState(false);
  const [redirectToSetup, setRedirectToSetup] = useState(false);
  const location = useLocation();

  // Check localStorage to see if we're in onboarding or have cached data
  const hasCachedOrg = !!localStorage.getItem("currentOrganisation");
  const hasCachedWebsite = !!localStorage.getItem("currentWebsiteId");
  const isOnboarding =
    !!localStorage.getItem("website_info") || !!localStorage.getItem("pending_signup");
  const isOnboardingPath = location.pathname === "/onboarding";

  // Safety timeout - if org loading takes too long, force-show content
  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      if (orgLoading || websitesLoading) {
        console.log(
          "Organization loading state persisted too long, showing content anyway"
        );
        setForceShowContent(true);
      }
    }, 3000);

    return () => clearTimeout(safetyTimer);
  }, [orgLoading, websitesLoading]);

  // After a delay, if we're sure the user has no org, redirect to setup
  useEffect(() => {
    if (
      !orgLoading &&
      !websitesLoading &&
      requireOrg &&
      !isOnboarding &&
      !isOnboardingPath
    ) {
      const setupCheckTimer = setTimeout(() => {
        if (!hasOrganisation && !currentWebsite && !hasCachedOrg && !hasCachedWebsite) {
          console.log("Confirmed user has no organization, redirecting to setup");
          setRedirectToSetup(true);
        }
      }, 1000);

      return () => clearTimeout(setupCheckTimer);
    }
  }, [
    orgLoading,
    websitesLoading,
    hasOrganisation,
    currentWebsite,
    hasCachedOrg,
    hasCachedWebsite,
    requireOrg,
    isOnboarding,
    isOnboardingPath,
  ]);

  // Skip checks if not required or during onboarding
  if (!requireOrg || isOnboarding || isOnboardingPath) {
    console.log("Skipping organization check:", {
      requireOrg,
      isOnboarding,
      isOnboardingPath,
    });
    return <>{children}</>;
  }

  // Redirect to setup if we're sure the user needs onboarding
  if (redirectToSetup) {
    return <Navigate to="/setup" replace />;
  }

  // Only show loading for a brief period, and only on first load
  if (
    orgLoading &&
    !currentWebsite &&
    !hasCachedOrg &&
    !hasCachedWebsite &&
    !forceShowContent &&
    !isOnboarding
  ) {
    return (
      <div className="flex items-center justify-center h-screen w-screen">
        <LoadingSpinner message="Checking organization..." />
      </div>
    );
  }

  return <>{children}</>;
};
